import { Button } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { ICertificate } from '@dtos/Certificate'
import { formatDateMedium } from '@utils/dateFormatter'
import { openWebBrowser } from '@utils/openWebBrowser'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Text, useMedia, XStack, YStack } from 'tamagui'
import { CoverImage } from '@components/CoverImage'

interface ICertificateCardProps {
  certificate: ICertificate
}

export function CertificateCard({ certificate }: ICertificateCardProps) {
  const { t } = useTranslation('Certificates')
  const media = useMedia()

  // Quando o certificado é por curso do combo (certification_per_course_path),
  // core seta certificate.course → mostra curso. Senão, mostra o produto (combo).
  // Mesma prioridade do hero_theme (snippets/school/certificates/list.liquid).
  const title = useMemo(
    () =>
      certificate.course?.name ||
      certificate.schoolProduct?.title ||
      t('card.fallbackTitle'),
    [certificate, t],
  )

  const thumbnail = useMemo(
    () => certificate.course?.imageUrl || certificate.schoolProduct?.logo || '',
    [certificate],
  )

  const completedAt = useMemo(
    () => formatDateMedium(certificate.updatedAt || certificate.createdAt),
    [certificate],
  )

  const handleDownload = useCallback(() => {
    if (!certificate.fileUrl) return
    openWebBrowser(certificate.fileUrl, true)
  }, [certificate.fileUrl])

  return (
    <YStack
      testID={`certificate-card-${certificate.id}`}
      backgroundColor="$bg-surface"
      br="$border-radius-md"
      borderWidth={1}
      borderColor="$border-subtle"
      padding="$size.spacing-xs"
      gap="$size.spacing-xs"
      width="100%"
      $gtSm={{
        flexDirection: 'row',
        alignItems: 'center',
        padding: '$size.spacing-sm',
      }}
    >
      <XStack flex={1} gap="$size.spacing-xs" alignItems="center">
        {thumbnail ? (
          <CoverImage
            testID="certificate-card-thumbnail"
            alt={title}
            uri={thumbnail}
            width={media.gtSm ? 160 : 112}
            borderRadius={4}
            style={{ aspectRatio: 16 / 9, flexShrink: 0 }}
          />
        ) : (
          <XStack
            width={media.gtSm ? 160 : 112}
            aspectRatio={16 / 9}
            br="$border-radius-sm"
            backgroundColor="$bg-surface-raised"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <HeroIcon
              icon={['fal', 'image']}
              color="$icon-tertiary"
              size={32}
            />
          </XStack>
        )}

        <YStack flex={1} gap="$size.spacing-nano">
          <Text
            numberOfLines={2}
            ff="$mulishBold"
            color="$text-primary"
            fontSize="$font-size-xs"
          >
            {title}
          </Text>
          <Text
            ff="$mulishRegular"
            color="$text-muted"
            fontSize="$font-size-xxs"
          >
            {t('card.completedAt', { date: completedAt })}
          </Text>
        </YStack>
      </XStack>

      <Button
        testID={`certificate-download-${certificate.id}`}
        variant="primary"
        onPress={handleDownload}
        disabled={!certificate.fileUrl}
        aria-label={t('card.download')}
        width="100%"
        $gtSm={{ width: 'auto' }}
      >
        {t('card.download')}
      </Button>
    </YStack>
  )
}
